import { Grid, Paper } from '@mui/material';

import { useSnackbar } from 'notistack';

import { useEffect, useState } from 'react';

import { SubmitHandler } from 'react-hook-form';

import { useMutation, useQuery } from 'react-query';

import { useNavigate, useParams } from 'react-router';

import { DashboardForm } from '.';

import * as api from '../../api/app/dashboardsApi';

import { ApiError } from '../../components/core/ApiError/ApiError';

import { AssetFullPathHeader } from '../../components/core/AssetFullPathHeader/AssetFullPathHeader';

import { Loader } from '../../components/core/Loader/Loader';

import { PageContent } from '../../components/core/PageContent/PageContent';

import { AssetDetailTabValues } from '../../types/app/asset.types';

import { Dashboard, DashboardInputs, DashboardTypes } from '../../types/app/dashboards.types';

import { dictionary } from '../../utils/core/dictionary';

import { convertDataFromInputToUpdateRequest } from '../../utils/core/functions/dashboard.functions';

import { paths } from '../../utils/core/routes';

import { DashbboardOpcSettingForm } from './DashbboardOpcSettingForm';

import {
  DashboardOpcSetting,
  DashboardOpcSettingInputs,
} from '../../types/app/dashboardOpcSetting';

import { useStyles } from './DashboardForm.styles';

import { DashboardOpcGrid } from './DashboardOpcGrid';

export function DashboardDetail() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id, parentId } = useParams();
  const [defaultValues, setDefaultValues] = useState<DashboardInputs | null>(null);

  const { mutate: mutateUpdateDashboard } = useMutation(api.updateDashboard);
  const { mutate: mutateUpdateDashboardOpcSetting } = useMutation(api.updateDashboardOpcSetting);
  const { data, isLoading, isError } = useQuery<Dashboard>(['dashboards', id], () =>
    api.getDashboard(Number(id)),
  );

  const { data: dataOpcSetting, refetch: refetchDataOpcSetting } = useQuery<DashboardOpcSetting>(
    ['dashboardOpcSetting', id],
    () => api.getDashboardOpcSetting(Number(id)),
    { enabled: !!data && data.type === DashboardTypes.OPC_DASHBOARD },
  );

  useEffect(() => {
    if (!data) return;

    setDefaultValues({
      id: data.id,
      name: data.name,
      assetId: data.assetId,
      type: data.type,
      url: data.url,
    });
  }, [data]);

  const handleOnSubmit: SubmitHandler<DashboardInputs> = (data: DashboardInputs) => {
    if (!defaultValues) return;

    const requestData = convertDataFromInputToUpdateRequest({
      ...data,
      id: Number(id!),
      assetId: Number(parentId),
    });
    mutateUpdateDashboard(
      { ...requestData, id: Number(id) },
      {
        onSuccess: () => {
          enqueueSnackbar(dictionary.DASHBOARD_EDITED, { variant: 'success' });
          navigate(`${paths.ASSETS_DETAIL}/${parentId}`, {
            state: {
              tabValue: AssetDetailTabValues.DASHBOARD,
            },
          });
        },
        onError: (result) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  const handleOnSubmitOpcSetting: SubmitHandler<DashboardOpcSettingInputs> = (
    data: DashboardOpcSettingInputs,
  ) => {
    if (!defaultValues) return;

    const requestData = { ...data, rows: Number(data.rows), columns: Number(data.columns) };
    mutateUpdateDashboardOpcSetting(
      { ...requestData },
      {
        onSuccess: () => {
          enqueueSnackbar(dictionary.DASHBOARD_EDITED, { variant: 'success' });
          refetchDataOpcSetting();
        },
        onError: (result) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  if (isError) return <ApiError />;
  if (isLoading || !defaultValues) return <Loader />;

  return (
    <>
      <AssetFullPathHeader finalTitle={dictionary.DASHBOARD_EDIT} parentId={Number(parentId)} />
      <PageContent>
        <Grid container spacing={3}>
          <Grid item xs={defaultValues.type === DashboardTypes.OPC_DASHBOARD ? 3 : 6}>
            <Paper className={classes.paper}>
              <DashboardForm
                onSubmit={handleOnSubmit}
                onBack={() =>
                  navigate(`${paths.ASSETS_DETAIL}/${parentId}`, {
                    state: {
                      tabValue: AssetDetailTabValues.DASHBOARD,
                    },
                  })
                }
                defaultValues={defaultValues}
                isEdit
              />
            </Paper>
            {defaultValues.type === DashboardTypes.OPC_DASHBOARD && dataOpcSetting && (
              <Paper className={classes.paper} style={{ marginTop: 10 }}>
                <DashbboardOpcSettingForm
                  defaultValues={dataOpcSetting}
                  onSubmit={handleOnSubmitOpcSetting}
                  onBack={() =>
                    navigate(`${paths.ASSETS_DETAIL}/${parentId}`, {
                      state: {
                        tabValue: AssetDetailTabValues.DASHBOARD,
                      },
                    })
                  }
                />
              </Paper>
            )}
          </Grid>
          {defaultValues.type === DashboardTypes.OPC_DASHBOARD && dataOpcSetting && (
            <Grid item xs={9}>
              <Paper className={classes.paper}>
                <DashboardOpcGrid dataOpcSetting={dataOpcSetting} assetId={Number(parentId)} />
              </Paper>
            </Grid>
          )}
        </Grid>
      </PageContent>
    </>
  );
}
